import React, { MouseEvent, useCallback, useEffect, useMemo } from 'react'
import { useNewTranslation } from 'hooks/index'
import ReactDOMServer from 'react-dom/server'
import { useDispatch, useSelector } from 'react-redux'
import ReactTooltip from 'react-tooltip'
import styled from 'particles/themes/styled'

import { selectLatestVoteAndDiscussion, voteSubmission, VoteType } from 'state/submissions'
import PopupUserInfo from './PopupUserInfo'
import {
    selectIsMultiSelectMode,
    selectSelectedCampaignUuid,
    makeShowCommentAndVoteSelector,
    SubmissionProgressProps,
    openDiscussionModal,
} from 'state/review'
import { StepState } from 'state/tasks'
import { selectCampaign, selectProfileDetails, State } from 'state'
import PartiIcon from 'particles/Icon'
import PartiText from 'particles/Text'
import { getCampaignSummary } from 'state/campaigns'

interface CommentAndVoteSummaryProps {
    submissionProgress: SubmissionProgressProps
    step: StepState
    className?: string
}

const CommentAndVoteSummary: React.FC<CommentAndVoteSummaryProps> = ({ submissionProgress, step, className }) => {
    const [t] = useNewTranslation()
    const dispatch = useDispatch()
    const selectedCampaignUuid = useSelector(selectSelectedCampaignUuid)
    const profile = useSelector(selectProfileDetails)
    const isMultiSelectMode = useSelector(selectIsMultiSelectMode)
    const campaign = useSelector(selectCampaign(selectedCampaignUuid as string))
    const selectShowCommentAndVote = useMemo(makeShowCommentAndVoteSelector, [])
    const showCommentAndVote = useSelector((state: State) => selectShowCommentAndVote(state, { step }))
    const { votes, discussion_members = [] } = useSelector(selectLatestVoteAndDiscussion(submissionProgress.uuid)) || {}

    const openCommentModal = () => {
        dispatch(openDiscussionModal({ step, submissionProgress, isOpen: true }))
        ReactTooltip.hide()
    }

    const isApproved = useMemo(() => votes && votes.approve.findIndex(item => item.uuid === profile.uuid) > -1, [votes])
    const isEditsRequired = useMemo(
        () => votes && votes.request_edits.findIndex(item => item.uuid === profile.uuid) > -1,
        [votes],
    )
    const isRejected = useMemo(() => votes && votes.reject.findIndex(item => item.uuid === profile.uuid) > -1, [votes])

    useEffect(() => {
        ReactTooltip.rebuild()
    })

    useEffect(() => {
        if (selectedCampaignUuid && !campaign) {
            dispatch(
                getCampaignSummary({
                    uuid: selectedCampaignUuid,
                }),
            )
        }
    }, [selectedCampaignUuid, dispatch, campaign])

    const handleVote = useCallback(
        (event: MouseEvent<HTMLDivElement, globalThis.MouseEvent>, vote_type: VoteType): void => {
            event.stopPropagation()
            if (selectedCampaignUuid) {
                dispatch(
                    voteSubmission({
                        campaignUuid: selectedCampaignUuid,
                        submissionProgressUuid: submissionProgress.uuid,
                        vote_type,
                        profile,
                    }),
                )
            }
        },
        [submissionProgress.uuid, selectedCampaignUuid],
    )

    if (!showCommentAndVote) {
        return null
    }

    return (
        <>
            <CommentAndVoteSummaryContainer
                className={className}
                isMultiSelectMode={isMultiSelectMode}
                onClick={e => e.stopPropagation()}
            >
                <div
                    className="button-container"
                    data-tip={ReactDOMServer.renderToStaticMarkup(
                        <TooltipContainer>
                            <PartiText scale="caption500" color="secondary">
                                {t('submission.review.approveSuggestionByHeader')}
                            </PartiText>
                            <PopupUserInfo userInfos={votes ? votes.approve : []} />
                        </TooltipContainer>,
                    )}
                    data-for="partiTooltip"
                    onClick={isMultiSelectMode ? undefined : e => handleVote(e, isApproved ? null : 'approve')}
                >
                    <PartiIcon name="checkCircle" size="20px" color="neutralAction" />
                    <PartiText scale="bodyL400" color="neutralAction">
                        {votes ? votes.approve.length : 0}
                    </PartiText>
                </div>
                <div
                    className="button-container"
                    data-tip={ReactDOMServer.renderToStaticMarkup(
                        <TooltipContainer>
                            <PartiText scale="caption500" color="secondary">
                                {t('submission.review.requestEditSuggestionByHeader')}
                            </PartiText>
                            <PopupUserInfo userInfos={votes ? votes.request_edits : []} />
                        </TooltipContainer>,
                    )}
                    data-for="partiTooltip"
                    onClick={e => {
                        if (isMultiSelectMode) {
                            return
                        }
                        handleVote(e, isEditsRequired ? null : 'request_edits')
                    }}
                >
                    <PartiIcon name="pencilSimple" size="20px" color="neutralAction" />
                    <PartiText scale="bodyL400" color="neutralAction">
                        {votes ? votes.request_edits.length : 0}
                    </PartiText>
                </div>
                <div
                    className="button-container"
                    data-tip={ReactDOMServer.renderToStaticMarkup(
                        <TooltipContainer>
                            <PartiText scale="caption500" color="secondary">
                                {t('submission.review.rejectSuggestionByHeader')}
                            </PartiText>
                            <PopupUserInfo userInfos={votes ? votes.reject : []} />
                        </TooltipContainer>,
                    )}
                    data-for="partiTooltip"
                    onClick={e => {
                        if (isMultiSelectMode) {
                            return
                        }
                        handleVote(e, isRejected ? null : 'reject')
                    }}
                >
                    <PartiIcon name="xCircle" size="20px" color="neutralAction" />
                    <PartiText scale="bodyL400" color="neutralAction">
                        {votes ? votes.reject.length : 0}
                    </PartiText>
                </div>
                <div
                    className="button-container"
                    data-tip={ReactDOMServer.renderToStaticMarkup(
                        <TooltipContainer>
                            <PartiText scale="caption500" color="secondary">
                                {t('submission.review.commentByHeader')}
                            </PartiText>
                            <PopupUserInfo userInfos={discussion_members} />{' '}
                        </TooltipContainer>,
                    )}
                    data-for="partiTooltip"
                    onClick={e => {
                        if (isMultiSelectMode) {
                            return
                        }
                        openCommentModal()
                    }}
                >
                    <PartiIcon name="chat" size="20px" color="neutralAction" />
                    <PartiText scale="bodyL400" color="neutralAction">
                        {discussion_members.length || 0}
                    </PartiText>
                </div>
            </CommentAndVoteSummaryContainer>
        </>
    )
}

const CommentAndVoteSummaryContainer = styled.div<{ isMultiSelectMode: boolean }>`
    display: flex;
    gap: ${({ theme }) => theme.spacing[300]};
    width: 100%;
    flex-wrap: wrap;

    .button-container {
        display: flex;
        align-items: center;
        width: 36px;
        max-width: 36px;
        gap: ${({ theme }) => theme.spacing[100]};
        opacity: ${({ isMultiSelectMode }) => (isMultiSelectMode ? 0.4 : 1)};
        cursor: pointer;
    }
`

const TooltipContainer = styled.div`
    min-width: fit-content;
    display: flex;
    flex-direction: column;
    gap: 8px;
`

export default React.memo(CommentAndVoteSummary)
